import { useState } from "react";
import { Banner, Button, Flex, QueryLoader, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { type MinimalQueryResult } from "@jsq/ds/types";
import { ExistingPositionsModal } from "~/pages/Compliance/ExistingPositionsModal";
import { CellValue } from "../CompareTable/CellValue";
import * as styles from "./styles_vanilla.css";
import { useHorizontalScrollArrow } from "./useHorizontalScrollArrow";
import { hasAccountsMismatch } from "./utils";
import type { CompareCell, InvestorCompareData } from "./types";
import type { AccountPositionsModalData } from "../CompareTable/utils";

export type InvestorToInvestorViewProps = {
  compareData: InvestorCompareData | null;
  queryResult: MinimalQueryResult;
};

type OnPositionsClick = (target: AccountPositionsModalData) => void;

const positionsLinkProps = (cell: CompareCell, onPositionsClick: OnPositionsClick) => {
  const target = cell.isLink ? cell.accountPositions : undefined;
  return target ? { isLink: true, onLinkClick: () => onPositionsClick(target) } : {};
};

const ExistingAccountValue = ({
  cell,
  onPositionsClick,
}: {
  cell: CompareCell;
  onPositionsClick: OnPositionsClick;
}) => {
  const linkProps = positionsLinkProps(cell, onPositionsClick);
  if (cell.status !== "mismatch") {
    return <CellValue cell={cell} {...linkProps} />;
  }
  return (
    <Flex alignItems="center" gap={Sizes.XS}>
      <CellValue cell={cell} {...linkProps} />
      <Icon type="exclamation-triangle" variant="outline" color={Colors.Warning600} />
    </Flex>
  );
};

const ComparisonTable = ({
  compareData,
  onPositionsClick,
}: {
  compareData: InvestorCompareData;
  onPositionsClick: OnPositionsClick;
}) => {
  const existingCount = compareData.existingAccountCount;

  return (
    <div className={styles.table}>
      <Flex className={styles.headerRow}>
        <div className={styles.labelCell} />
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          className={`${styles.valueCell} ${styles.currentCell}`}
        >
          <Text variant="secondary-md">Investment account</Text>
          <Text variant="regular-md">{compareData.currentPositionSubtitle}</Text>
        </Flex>
        <div
          className={styles.existingHeaderCell}
          style={{ width: existingCount * styles.VALUE_COLUMN_WIDTH }}
        >
          <div className={styles.existingHeaderTitle}>
            <Text variant="secondary-md">{compareData.existingAccountsHeader}</Text>
          </div>
        </div>
      </Flex>
      {compareData.rows.map((rowItem) => (
        <Flex key={rowItem.label} className={styles.row}>
          <Text variant="secondary-md" className={styles.labelCell}>
            {rowItem.label}
          </Text>
          <div className={`${styles.valueCell} ${styles.currentCell}`}>
            <CellValue
              cell={rowItem.currentPosition}
              {...positionsLinkProps(rowItem.currentPosition, onPositionsClick)}
            />
          </div>
          {rowItem.existingAccounts.map((accountCell, index) => (
            <div key={`${rowItem.label}-${index}`} className={styles.valueCell}>
              <ExistingAccountValue cell={accountCell} onPositionsClick={onPositionsClick} />
            </div>
          ))}
        </Flex>
      ))}
    </div>
  );
};

export const InvestorToInvestorView = ({
  compareData,
  queryResult,
}: InvestorToInvestorViewProps) => {
  const { scrollContainerRef, canScrollRight, scrollToEnd } = useHorizontalScrollArrow();
  const [target, setTarget] = useState<AccountPositionsModalData | null>(null);

  return (
    <QueryLoader result={queryResult}>
      {compareData ? (
        <Flex flexDirection="column" gap={Sizes.MD}>
          {hasAccountsMismatch(compareData) && (
            <Banner
              variant="warning"
              title="Review the profile validation discrepancy"
              description="The existing investment accounts associated with the selected compliance profile have details that do not match the investment account information. You can still continue or select a different profile."
            />
          )}
          <div className={styles.wrapper}>
            <div className={styles.scrollContainer} ref={scrollContainerRef}>
              <ComparisonTable compareData={compareData} onPositionsClick={setTarget} />
            </div>
            {canScrollRight && (
              <Button
                variant="muted"
                className={styles.scrollArrowButton}
                onClick={scrollToEnd}
                aria-label="Scroll to last existing account"
                childAsIcon={{
                  type: "chevron-right",
                  variant: "outline",
                  color: Colors.Neutral800,
                  size: "large",
                }}
              />
            )}
            {target && (
              <ExistingPositionsModal
                accountName={target.accountName}
                rows={target.rows}
                viewAllHref={target.viewAllHref}
                onClose={() => setTarget(null)}
              />
            )}
          </div>
        </Flex>
      ) : (
        <Text variant="regular-md">
          No existing investment accounts found for this profile.
        </Text>
      )}
    </QueryLoader>
  );
};
